import type { PartnerRule } from '@shipium-corp/shipium-ts-types';
import GatewayAxiosClient from '~/store/services/GatewayAxiosClient';

interface MastraExplainResponse {
  text: string;
}

export const explainShipment = {
  queryKey: 'mastra-service/explain/shipment/id',
  configurationType: 'cached' as const,
  fn: async (shipmentId: string) => {
    const { data } = await GatewayAxiosClient.get<MastraExplainResponse>(
      `/mastra-proxy/explain/shipment/${shipmentId}`,
    );

    return data?.text;
  },
};

export interface DraftRuleCriterion {
  rulePropertyEnum: string;
  ruleComparatorEnum: string;
  compareValue: string | number | boolean | Array<string | number>;
  scalarUnitEnum?: string;
}

export interface DraftRuleResponse {
  draft: {
    name: string;
    description?: string;
    criteria: DraftRuleCriterion[];
    criteriaMatchStrategy: 'all_match' | 'any_match';
    carrierAndServiceMethodList: string[];
    tenantIds?: string[];
    listType: 'allow' | 'block' | 'add';
    terminating: boolean;
  };
  clarifications: string[];
  rationale: string;
  modelUsed: string;
  validationPassed: boolean;
}

export interface AvailableCarrier {
  id: string;
  name: string;
}

export interface AvailableTenant {
  id: string;
  name: string;
}

export interface AvailableOrigin {
  id: string;
  name: string;
}

export interface DescribeRuleResponse {
  description: string;
  modelUsed: string;
}

export interface NamedRef {
  id: string;
  name: string;
}

export const describeRule = async (
  rule: PartnerRule,
  carrierNames?: NamedRef[],
  tenantNames?: NamedRef[],
): Promise<DescribeRuleResponse> => {
  // Mastra's built-in tool execute route expects the tool input under `data`
  // and a (possibly empty) `requestContext` object.
  const { data } = await GatewayAxiosClient.post<DescribeRuleResponse>(
    `/mastra-proxy/api/tools/describe-rule/execute`,
    {
      data: { rule, carrierNames, tenantNames },
      requestContext: {},
    },
  );
  return data;
};

export const draftRuleFromText = async (
  text: string,
  availableCarriers?: AvailableCarrier[],
  availableTenants?: AvailableTenant[],
  availableOrigins?: AvailableOrigin[],
): Promise<DraftRuleResponse> => {
  // Mastra's built-in tool execute route expects the tool input under `data`
  // and a (possibly empty) `requestContext` object.
  const { data } = await GatewayAxiosClient.post<DraftRuleResponse>(
    `/mastra-proxy/api/tools/draft-rule-from-text/execute`,
    {
      data: { text, availableCarriers, availableTenants, availableOrigins },
      requestContext: {},
    },
  );
  return data;
};
